/**
 * Violation Domain Logic
 *
 * Pure functions for determining property violation status.
 * No external dependencies (firebase, models, etc.).
 */
import {
  LicenseStatusType,
  computeLicenseStatusType,
  isLicenseActive
} from './license.js';
import { isLicenseExpired } from './licenseExpiration.js';

// Re-export for backward compatibility
export { LicenseStatusType, computeLicenseStatusType, isLicenseActive };

/**
 * Violation status values
 */
export const ViolationStatus = Object.freeze({
  violation: 'violation',
  clear: 'clear',
  unknown: 'unknown'
});

/**
 * Violation reason type values
 */
export const ViolationReason = Object.freeze({
  noLicense: 'noLicense',
  expiredLicense: 'expiredLicense',
  inactiveLicense: 'inactiveLicense'
});

/**
 * Priority order for license status types (index = priority, higher is better)
 */
const LICENSE_STATUS_PRIORITY = Object.freeze([
  LicenseStatusType.none,
  LicenseStatusType.expired,
  LicenseStatusType.inactive,
  LicenseStatusType.pending,
  LicenseStatusType.active
]);

/**
 * Get the priority value for a license status type (higher = better).
 *
 * @param {string} statusType - One of the LicenseStatusType values
 * @returns {number} Priority value (0-4, higher is better)
 */
export function getLicenseStatusPriority(statusType) {
  const index = LICENSE_STATUS_PRIORITY.indexOf(statusType);
  return index >= 0 ? index : 0;
}

/**
 * Compute the violation status for a property.
 *
 * A property is in violation if:
 * 1. It does NOT have an active license (active or pending), AND
 * 2. It has at least one active listing
 *
 * @param {Object} params
 * @param {string} params.licenseStatusType - The property's license status type
 * @param {number} params.activeListingCount - Number of active listings on the property
 * @returns {string} One of: 'violation', 'clear'
 */
export function computeViolationStatus({
  licenseStatusType,
  activeListingCount
}) {
  const hasActiveLicense = isLicenseActive(licenseStatusType);
  const hasActiveListings = (activeListingCount || 0) > 0;

  const isViolation = !hasActiveLicense && hasActiveListings;

  return isViolation ? ViolationStatus.violation : ViolationStatus.clear;
}

/**
 * Select the "best" license from a list based on status priority and recency.
 *
 * Priority order (highest to lowest):
 * 1. active
 * 2. pending
 * 3. inactive
 * 4. expired
 * 5. none
 *
 * Within the same priority, more recent licenses (by dateCreated) are preferred.
 *
 * @param {Object} params
 * @param {Array<Object>} params.licenses - Array of license objects
 * @param {Array<{code: string, type: string}>} params.statusConfig - City's configured status types
 * @param {Function} [params.getWorkflowStatus] - Function to extract workflow status from license (default: l => l.status)
 * @param {Function} [params.getIsExpired] - Function to check if license is expired (default: expires on or before today)
 * @param {Function} [params.getDateCreated] - Function to extract creation date (default: l => l.dateCreated)
 * @returns {Object|null} The best license, or null if no licenses
 */
export function selectBestLicense({
  licenses,
  statusConfig = [],
  getWorkflowStatus = (l) => l.status,
  getIsExpired = (l) => isLicenseExpired(l.expiresOn),
  getDateCreated = (l) => l.dateCreated
}) {
  if (!licenses || licenses.length === 0) {
    return null;
  }

  // Compute status type for each license and sort by priority (descending), then by date (descending)
  const licensesWithPriority = licenses.map(license => {
    const statusType = computeLicenseStatusType({
      workflowStatus: getWorkflowStatus(license),
      isExpired: getIsExpired(license),
      statusConfig
    });
    const priority = getLicenseStatusPriority(statusType);
    const dateCreated = getDateCreated(license);
    return { license, statusType, priority, dateCreated };
  });

  // Sort: higher priority first, then more recent first
  licensesWithPriority.sort((a, b) => {
    if (a.priority !== b.priority) {
      return b.priority - a.priority; // Higher priority first
    }
    // Same priority: sort by date (more recent first)
    const dateA = a.dateCreated instanceof Date ? a.dateCreated.getTime() : (a.dateCreated || 0);
    const dateB = b.dateCreated instanceof Date ? b.dateCreated.getTime() : (b.dateCreated || 0);
    return dateB - dateA;
  });

  return licensesWithPriority[0]?.license || null;
}

/**
 * Compute complete violation data for a property.
 *
 * This is the main entry point that combines all violation logic.
 *
 * @param {Object} params
 * @param {Array<Object>} params.licenses - Array of license objects for this property
 * @param {number} params.activeListingCount - Number of active listings on the property
 * @param {Array<{code: string, type: string}>} params.statusConfig - City's configured status types
 * @param {Function} [params.getWorkflowStatus] - Function to extract workflow status from license
 * @param {Function} [params.getIsExpired] - Function to check if license is expired (default: expires on or before today)
 * @param {Function} [params.getDateCreated] - Function to extract creation date
 * @returns {Object} Violation data: { status, licenseStatusType, licenseWorkflowStatus, activeListingCount, bestLicense }
 */
export function computePropertyViolationData({
  licenses = [],
  activeListingCount = 0,
  statusConfig = [],
  getWorkflowStatus = (l) => l.status,
  getIsExpired = (l) => isLicenseExpired(l.expiresOn),
  getDateCreated = (l) => l.dateCreated
}) {
  // Find the best license
  const bestLicense = selectBestLicense({
    licenses,
    statusConfig,
    getWorkflowStatus,
    getIsExpired,
    getDateCreated
  });

  // Compute the license status type
  const licenseStatusType = bestLicense
    ? computeLicenseStatusType({
        workflowStatus: getWorkflowStatus(bestLicense),
        isExpired: getIsExpired(bestLicense),
        statusConfig
      })
    : LicenseStatusType.none;

  // Compute violation status
  const status = computeViolationStatus({
    licenseStatusType,
    activeListingCount
  });

  return {
    status,
    licenseStatusType,
    licenseWorkflowStatus: bestLicense ? getWorkflowStatus(bestLicense) : null,
    activeListingCount,
    bestLicense
  };
}

/**
 * Check if a listing is a violation (simplified check for client-side use).
 *
 * A listing is considered a violation if:
 * - It has no associated license, OR
 * - Its associated license is inactive or expired
 *
 * Note: This is a simplified check that matches the client-side AppModel.isViolation() behavior.
 * For full violation checking, use computePropertyViolationData().
 *
 * @param {Object} params
 * @param {Object|null} params.license - The license associated with the listing's property
 * @param {string|null} [params.licenseStatusType] - Pre-computed license status type (optional optimization)
 * @param {Array<{code: string, type: string}>} [params.statusConfig] - City's configured status types (needed if licenseStatusType not provided)
 * @param {Function} [params.getWorkflowStatus] - Function to extract workflow status from license
 * @param {Function} [params.getIsExpired] - Function to check if license is expired (default: expires on or before today)
 * @returns {boolean} True if the listing is a violation
 */
export function isListingViolation({
  license,
  licenseStatusType = null,
  statusConfig = [],
  getWorkflowStatus = (l) => l.status,
  getIsExpired = (l) => isLicenseExpired(l.expiresOn)
}) {
  // No license = violation
  if (!license) {
    return true;
  }

  // If license has statusWithConfig (client License model), use it directly
  if (license.statusWithConfig) {
    const type = license.statusWithConfig.type;
    return type === LicenseStatusType.inactive || type === LicenseStatusType.expired;
  }

  // Compute status type if not provided
  const statusType = licenseStatusType || computeLicenseStatusType({
    workflowStatus: getWorkflowStatus(license),
    isExpired: getIsExpired(license),
    statusConfig
  });

  // Inactive or expired license = violation
  return statusType === LicenseStatusType.inactive || statusType === LicenseStatusType.expired;
}
